import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import fetchApi from '../utils/api.js';
import PageSkeleton from '../components/ui/PageSkeleton';
import SearchBar from '../components/ui/SearchBar';
import AirDatePicker from '../components/ui/AirDatePicker';
import DataTable from '../components/ui/DataTable';

function VoucherTransactions({ isReport = false }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [voucherType, setVoucherType] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    // Filters
    const [draftStartDate, setDraftStartDate] = useState('');
    const [draftEndDate, setDraftEndDate] = useState('');
    const [draftType, setDraftType] = useState('All Types');

    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');
    const [appliedType, setAppliedType] = useState('All Types');

    const fetchData = async () => {
        try {
            setLoading(true);
            const userStr = sessionStorage.getItem('user');
            if (!userStr) return;
            const user = JSON.parse(userStr);
            const companyId = user.companyId || user.company_id;

            console.log('[VoucherTransactions] Fetching isReport:', isReport, 'voucherTypeId:', id, 'companyId:', companyId);

            const endpoint = isReport 
                ? `/voucher-entry/all-payments?companyId=${companyId}`
                : `/voucher-entry/${id}?companyId=${companyId}`;

            const result = await fetchApi(endpoint);

            console.log('[VoucherTransactions] API result:', result);
            
            if (result && Array.isArray(result)) {
                if (isReport) {
                    const flatList = result.map(item => ({
                        account: item.account || '',
                        accountGroupName: item.accountGroupName || 'Sales Account',
                        paymentType: item.paymentType || '',
                        paymentAmount: item.paymentAmount || 0,
                        paymentDate: item.paymentDate || '',
                        checkNumber: item.checkNumber || '',
                        remark: item.remark || '',
                        voucherTypeName: item.voucherTypeName || '',
                        paymentCollector: item.paymentCollector || 'N/A'
                    }));
                    flatList.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
                    setLedger(flatList);
                } else {
                    // First set the voucher type name from first record
                    const firstWithVT = result.find(r => r.voucherType && (r.voucherType.voucherTypeName || r.voucherType.voucher_type_name));
                    if (firstWithVT) {
                        setVoucherType({
                            ...firstWithVT.voucherType,
                            voucherTypeName: firstWithVT.voucherType.voucherTypeName || firstWithVT.voucherType.voucher_type_name
                        });
                    }

                    let flatList = [];
                    result.forEach(doc => {
                        if (doc.entries && Array.isArray(doc.entries)) {
                            doc.entries.forEach(e => {
                                // handle both camelCase and snake_case from backend
                                flatList.push({
                                    account: e.account || e.accountName || '',
                                    paymentType: e.paymentType || e.payment_type || '',
                                    paymentAmount: e.paymentAmount || e.payment_amount || 0,
                                    paymentDate: e.paymentDate || e.payment_date || '',
                                    checkNumber: e.checkNumber || e.check_number || '',
                                    remark: e.remark || '',
                                    voucherTypeName: doc.voucherType?.voucherTypeName || doc.voucherType?.voucher_type_name || '',
                                    paymentCollector: doc.createdBy?.fullName || e.paymentCollector || 'N/A'
                                });
                            });
                        }
                    });

                    console.log('[VoucherTransactions] flatList:', flatList);

                    // Sort by paymentDate descending
                    flatList.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
                    setLedger(flatList);

                    // If no voucherType yet, fetch it separately
                    if (!firstWithVT && id) {
                        const vt = await fetchApi(`/voucher-type/${id}`);
                        if (vt) setVoucherType(vt);
                    }
                }
            } else {
                if (!isReport && id) {
                    const vt = await fetchApi(`/voucher-type/${id}`);
                    if (vt) setVoucherType(vt);
                }
            }
        } catch (error) {
            console.error('Error fetching voucher ledger:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isReport]);

    const handleSubmitFilters = () => {
        setAppliedStartDate(draftStartDate);
        setAppliedEndDate(draftEndDate);
        setAppliedType(draftType);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setDraftStartDate('');
        setDraftEndDate('');
        setDraftType('All Types');
        setAppliedStartDate('');
        setAppliedEndDate('');
        setAppliedType('All Types');
        setSearchQuery('');
        setCurrentPage(1);
    };

    const handleExcelDownload = () => {
        try {
            const worksheetData = filteredLedger.map((r, idx) => ({
                'No.': idx + 1,
                'NAME / TRADE NAME': r.account || '',
                'ACCOUNT GROUP NAME': r.accountGroupName || '',
                'TRANSACTION TYPE': r.paymentType || '',
                'VOUCHER TYPE': r.voucherTypeName || '',
                'PAYMENT AMOUNT': parseFloat(r.paymentAmount || 0).toFixed(2),
                'PAYMENT DATE': r.paymentDate ? new Date(r.paymentDate).toLocaleDateString('en-GB') : '',
                'CHECK NUMBER': r.checkNumber || '',
                'REMARK': r.remark || '',
                'PAYMENT COLLECTOR': r.paymentCollector || ''
            }));

            const totalAmt = filteredLedger.reduce((sum, item) => sum + (Number(item.paymentAmount) || 0), 0).toFixed(2);
            worksheetData.push({
                'No.': 'TOTAL',
                'NAME / TRADE NAME': '',
                'ACCOUNT GROUP NAME': '',
                'TRANSACTION TYPE': '',
                'VOUCHER TYPE': '',
                'PAYMENT AMOUNT': totalAmt,
                'PAYMENT DATE': '',
                'CHECK NUMBER': '',
                'REMARK': '',
                'PAYMENT COLLECTOR': ''
            });

            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments');
            XLSX.writeFile(workbook, 'PaymentList.xlsx');
            toast.success("Excel exported successfully!");
        } catch (error) {
            console.error("Excel export error:", error);
            toast.error("Failed to export excel");
        }
    };

    const handlePrint = () => {
        try {
            const printWindow = window.open('', '', 'height=600,width=800');
            printWindow.document.write('<html><head><title>Payment List</title>');
            printWindow.document.write('<style>');
            printWindow.document.write('@page { size: A4 landscape; margin: 10mm; }');
            printWindow.document.write('body { font-family: Arial, sans-serif; margin: 0; padding: 10px; }');
            printWindow.document.write('h1 { text-align: center; color: #0b3a54; font-size: 16px; margin-bottom: 10px; }');
            printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 5px; table-layout: auto; }');
            printWindow.document.write('th, td { border: 1px solid #ccc; padding: 6px 4px; text-align: left; font-size: 9px; word-break: break-word; }');
            printWindow.document.write('th { background-color: #dff5ea; color: #000; font-weight: 700; text-transform: uppercase; }');
            printWindow.document.write('td.right { text-align: right; }');
            printWindow.document.write('</style></head><body>');
            printWindow.document.write('<h1>Payment List</h1>');
            printWindow.document.write('<table><thead><tr>');
            printWindow.document.write('<th>No.</th>');
            printWindow.document.write('<th>NAME / TRADE NAME</th>');
            printWindow.document.write('<th>ACCOUNT GROUP NAME</th>');
            printWindow.document.write('<th>TRANSACTION TYPE</th>');
            printWindow.document.write('<th>VOUCHER TYPE</th>');
            printWindow.document.write('<th>PAYMENT AMOUNT</th>');
            printWindow.document.write('<th>PAYMENT DATE</th>');
            printWindow.document.write('<th>CHECK NUMBER</th>');
            printWindow.document.write('<th>REMARK</th>');
            printWindow.document.write('<th>PAYMENT COLLECTOR</th>');
            printWindow.document.write('</tr></thead><tbody>');

            filteredLedger.forEach((r, index) => {
                printWindow.document.write('<tr>');
                printWindow.document.write(`<td>${index + 1}</td>`);
                printWindow.document.write(`<td>${r.account || ''}</td>`);
                printWindow.document.write(`<td>${r.accountGroupName || ''}</td>`);
                printWindow.document.write(`<td>${r.paymentType || ''}</td>`);
                printWindow.document.write(`<td>${r.voucherTypeName || ''}</td>`);
                printWindow.document.write(`<td class="right">${parseFloat(r.paymentAmount || 0).toFixed(2)}</td>`);
                printWindow.document.write(`<td>${r.paymentDate ? new Date(r.paymentDate).toLocaleDateString('en-GB') : ''}</td>`);
                printWindow.document.write(`<td>${r.checkNumber || ''}</td>`);
                printWindow.document.write(`<td>${r.remark || ''}</td>`);
                printWindow.document.write(`<td>${r.paymentCollector || ''}</td>`);
                printWindow.document.write('</tr>');
            });

            const totalAmtPrint = filteredLedger.reduce((sum, item) => sum + (Number(item.paymentAmount) || 0), 0).toFixed(2);
            printWindow.document.write('<tr style="font-weight: bold; background-color: #eaeaea;">');
            printWindow.document.write('<td colspan="5" style="text-align: right; font-weight: bold;">TOTAL:</td>');
            printWindow.document.write(`<td class="right" style="font-weight: bold;">${totalAmtPrint}</td>`);
            printWindow.document.write('<td colspan="4"></td>');
            printWindow.document.write('</tr>');

            printWindow.document.write('</tbody></table></body></html>');
            printWindow.document.close();
            setTimeout(() => {
                printWindow.print();
            }, 500);
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Failed to print data");
        }
    };

    const filteredLedger = useMemo(() => {
        let list = [...ledger];

        // Date Filter
        if (appliedStartDate) {
            list = list.filter(item => new Date(item.paymentDate) >= new Date(appliedStartDate));
        }
        if (appliedEndDate) {
            list = list.filter(item => new Date(item.paymentDate) <= new Date(appliedEndDate));
        }

        // Transaction Type Filter
        if (appliedType !== 'All Types') {
            list = list.filter(item => item.paymentType === appliedType);
        }

        // Search Filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            list = list.filter(item => 
                (item.account || '').toLowerCase().includes(q) ||
                (item.remark || '').toLowerCase().includes(q) ||
                (item.checkNumber || '').toLowerCase().includes(q) ||
                (item.paymentCollector || '').toLowerCase().includes(q) ||
                (item.accountGroupName || '').toLowerCase().includes(q) ||
                (item.voucherTypeName || '').toLowerCase().includes(q)
            );
        }

        return list;
    }, [ledger, appliedStartDate, appliedEndDate, appliedType, searchQuery]);

    const totalPages = Math.ceil(filteredLedger.length / pageSize);
    const pagedRows = filteredLedger.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const showingFrom = filteredLedger.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const showingTo = Math.min(filteredLedger.length, currentPage * pageSize);

    const pages = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [currentPage, totalPages]);

    if (loading) return <PageSkeleton />;

    const columnsReport = [
        { key: 'account', label: 'NAME / TRADE NAME', style: { fontWeight: '500' } },
        { key: 'accountGroupName', label: 'ACCOUNT GROUP NAME' },
        { key: 'paymentType', label: 'TRANSACTION TYPE' },
        { key: 'voucherTypeName', label: 'VOUCHER TYPE' },
        { key: 'paymentAmount', label: 'PAYMENT AMOUNT', style: { fontWeight: '600' }, render: (item) => Number(item.paymentAmount).toFixed(2) },
        { key: 'paymentDate', label: 'PAYMENT DATE', render: (item) => item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('en-GB') : '' },
        { key: 'checkNumber', label: 'CHECK NUMBER', render: (item) => item.checkNumber || '' },
        { key: 'remark', label: 'REMARK', render: (item) => item.remark || '' },
        { key: 'paymentCollector', label: 'PAYMENT COLLECTOR' }
    ];

    const columnsNormal = [
        { key: 'account', label: 'NAME/TRADE NAME', style: { fontWeight: '500' } },
        { key: 'paymentType', label: 'TRANSACTION TYPE' },
        { key: 'paymentAmount', label: 'PAYMENT AMOUNT', style: { fontWeight: '600' }, render: (item) => Number(item.paymentAmount).toFixed(2) },
        { key: 'paymentDate', label: 'PAYMENT DATE', render: (item) => item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('en-GB') : '' },
        { key: 'checkNumber', label: 'CHECK NUMBER', render: (item) => item.checkNumber || '' },
        { key: 'remark', label: 'REMARK', render: (item) => item.remark || '' },
        { key: 'paymentCollector', label: 'PAYMENT COLLECTOR' }
    ];

    const columns = isReport ? columnsReport : columnsNormal;

    const footerRow = isReport ? (
        <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold', color: '#1e293b' }}>TOTAL</td>
            <td style={{ fontWeight: 'bold', color: '#1e293b' }}>
                {filteredLedger.reduce((sum, item) => sum + (Number(item.paymentAmount) || 0), 0).toFixed(2)}
            </td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
    ) : (
        <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
            <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold', color: '#1e293b' }}>TOTAL</td>
            <td style={{ fontWeight: 'bold', color: '#1e293b' }}>
                {filteredLedger.reduce((sum, item) => sum + (Number(item.paymentAmount) || 0), 0).toFixed(2)}
            </td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
        </tr>
    );

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">{isReport ? 'Payment List' : `${voucherType?.voucherTypeName} - All Payment Details`}</h1>
                {isReport ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="list-page-btn list-page-btn-excel" 
                          style={{ fontSize: '13px', height: '32px', padding: '0 15px', fontWeight: 'bold' }}
                          onClick={handleExcelDownload}
                        >
                          EXCEL
                        </button>
                        <button 
                          className="list-page-btn list-page-btn-print" 
                          style={{ fontSize: '13px', height: '32px', padding: '0 15px', fontWeight: 'bold' }}
                          onClick={handlePrint}
                        >
                          PRINT
                        </button>
                    </div>
                ) : (
                    <button 
                      className="list-page-btn" 
                      onClick={() => navigate(-1)}
                      style={{ background: '#64748b', color: '#fff', fontSize: '13px', height: '32px', padding: '0 15px' }}
                    >
                      Back
                    </button>
                )}
            </div>

            {/* Toolbar split: SEARCH on the left, other filters on the right */}
            <div className="list-page-toolbar" style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', alignItems: 'flex-start', background: 'transparent', padding: '10px 0', boxShadow: 'none', marginBottom: '5px' }}>
                <SearchBar
                    label="SEARCH"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                    }}
                    placeholder="Search transactions..."
                    style={{ width: '280px' }}
                />

                <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-start' }}>
                    <div className="list-page-filter-group" style={{ flex: '0 0 155px' }}>
                        <label className="list-page-filter-label" style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px', display: 'block', textTransform: 'none' }}>Start Date</label>
                        <AirDatePicker
                            value={draftStartDate}
                            onChange={(val) => setDraftStartDate(val)}
                            placeholder="Start Date"
                            className="list-page-filter-date"
                        />
                    </div>

                    <div className="list-page-filter-group" style={{ flex: '0 0 155px' }}>
                        <label className="list-page-filter-label" style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px', display: 'block', textTransform: 'none' }}>End Date</label>
                        <AirDatePicker
                            value={draftEndDate}
                            onChange={(val) => setDraftEndDate(val)}
                            placeholder="End Date"
                            className="list-page-filter-date"
                        />
                    </div>

                    <div className="list-page-filter-group" style={{ flex: '0 0 195px' }}>
                        <label className="list-page-filter-label" style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b', marginBottom: '2px', display: 'block', textTransform: 'none' }}>Transaction Type</label>
                        <select 
                          className="list-page-filter-select" 
                          value={draftType} 
                          onChange={(e) => setDraftType(e.target.value)}
                          style={{ height: '38px', borderRadius: '8px', fontSize: '13px' }}
                        >
                            <option value="All Types">---Select Type---</option>
                            <option value="Credit">Credit</option>
                            <option value="Debit">Debit</option>
                        </select>
                    </div>

                    <div style={{ display: 'flex', gap: '2px', marginTop: '25px' }}>
                        <button 
                          className="list-page-btn list-page-btn-submit" 
                          style={{ height: '38px', fontWeight: 'bold', minWidth: '85px', borderRadius: '6px', padding: '0 10px', fontSize: '14px' }}
                          onClick={handleSubmitFilters}
                        >
                          Filter
                        </button>
                        <button 
                          className="list-page-btn list-page-btn-reset" 
                          style={{ height: '38px', fontWeight: 'bold', minWidth: '85px', borderRadius: '6px', padding: '0 10px', fontSize: '14px' }}
                          onClick={handleClearFilters}
                        >
                          Clear
                        </button>
                    </div>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={filteredLedger}
                loading={loading}
                emptyMessage="No transactions found"
                page={currentPage}
                pageSize={pageSize}
                totalEntries={filteredLedger.length}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                footerRow={footerRow}
                headerStyle={{ background: '#dff5ea', color: '#0f172a', fontWeight: '700', textTransform: 'uppercase' }}
            />
        </div>
    );
}

export default VoucherTransactions;
